/**
 * Optional selection range for question types `multiple` and `multiple-image`.
 *
 * Stored on the question (null or absent = that bound is off):
 * - minSelections (J)
 * - maxSelections (N)
 *
 * Respondent rules:
 * - neither set → unlimited; `required` still means at least 1
 * - only max, not required → 0..N
 * - only max, required → 1..N (`required` supplies the minimum; minSelections stays unset)
 * - only min → at least J, no upper cap (blocks Next even when required is false)
 * - both set → J..N
 *
 * A real choice is a non-empty value other than the `__custom__` marker.
 * Custom text counts as one choice. The pending `__custom__` marker does not
 * count toward the minimum, but it reserves a slot toward the maximum so the
 * respondent cannot go over N after typing.
 *
 * Builder: J and N are integers ≥ 1, N ≥ J, and neither may exceed the number
 * of non-empty options. The custom option does not raise that ceiling.
 */

const MULTI_SELECT_TYPES = ['multiple', 'multiple-image'];

export function isMultiSelectQuestion(question) {
  return !!question && MULTI_SELECT_TYPES.includes(question.type);
}

export function parseBound(value) {
  if (value === null || value === undefined || value === '') return null;
  const raw = typeof value === 'number' ? value : Number(String(value).trim());
  if (!Number.isInteger(raw) || raw < 1) return null;
  return raw;
}

export function getSelectionLimits(question) {
  if (!isMultiSelectQuestion(question)) return { min: null, max: null };
  return {
    min: parseBound(question.minSelections),
    max: parseBound(question.maxSelections),
  };
}

export function countRealSelections(answer) {
  if (!Array.isArray(answer)) return 0;
  const seen = new Set();
  for (const item of answer) {
    if (item === '__custom__' || item == null) continue;
    const text = String(item).trim();
    if (!text || text === '__custom__') continue;
    seen.add(text);
  }
  return seen.size;
}

/** Real choices plus a reserved slot while the custom marker is checked but empty. */
export function countSelectionSlots(answer) {
  if (!Array.isArray(answer)) return 0;
  const hasPendingCustom = answer.some((item) => item === '__custom__');
  return countRealSelections(answer) + (hasPendingCustom ? 1 : 0);
}

export function selectionHint(question) {
  const { min, max } = getSelectionLimits(question);
  if (min != null && max != null) return `Выберите от ${min} до ${max}`;
  if (min != null) return `Выберите не меньше ${min}`;
  if (max != null) return `Выберите не больше ${max}`;
  return null;
}

export function selectionCardSuffix(question) {
  const { min, max } = getSelectionLimits(question);
  if (min != null && max != null) return ` • от ${min} до ${max}`;
  if (min != null) return ` • не меньше ${min}`;
  if (max != null) return ` • не больше ${max}`;
  return '';
}

/** True when the current answer is outside an explicit J..N bound. */
export function multiSelectBlocksProgress(question, answer) {
  const { min, max } = getSelectionLimits(question);
  if (min == null && max == null) return false;
  const count = countRealSelections(answer);
  if (min != null && count < min) return true;
  if (max != null && count > max) return true;
  return false;
}

export function selectionProgressMessage(question, answer) {
  if (!multiSelectBlocksProgress(question, answer)) return null;
  return selectionHint(question);
}

export function isSelectionCapped(question, answer) {
  const { max } = getSelectionLimits(question);
  if (max == null) return false;
  return countSelectionSlots(answer) >= max;
}

export function countBuilderOptions(question) {
  if (!question) return 0;
  if (question.type === 'multiple') {
    return (question.options || []).filter((option) => String(option || '').trim()).length;
  }
  if (question.type === 'multiple-image') {
    return (question.imageOptions || []).filter(
      (option) => option && String(option.text || '').trim() && option.imageUrl
    ).length;
  }
  return 0;
}

function parseBuilderBound(value) {
  if (value === null || value === undefined) return { value: null };
  const raw = String(value).trim();
  if (raw === '' || !/^\d+$/.test(raw)) return { error: true };
  const number = Number(raw);
  if (number < 1) return { error: true };
  return { value: number };
}

/**
 * Normalize min/max for save. Non-multi types always store null.
 * Returns `{ question }` or `{ error }`.
 */
export function normalizeBuilderSelectionLimits(question) {
  if (!isMultiSelectQuestion(question)) {
    return {
      question: {
        ...question,
        minSelections: null,
        maxSelections: null,
      },
    };
  }

  const optionCount = countBuilderOptions(question);
  const minParsed = parseBuilderBound(question.minSelections);
  const maxParsed = parseBuilderBound(question.maxSelections);
  if (minParsed.error || (minParsed.value != null && minParsed.value > optionCount)) {
    return { error: 'Минимум должен быть целым числом от 1 до числа вариантов' };
  }
  if (maxParsed.error || (maxParsed.value != null && maxParsed.value > optionCount)) {
    return { error: 'Максимум должен быть целым числом от 1 до числа вариантов' };
  }
  if (minParsed.value != null && maxParsed.value != null && maxParsed.value < minParsed.value) {
    return { error: 'Максимум не может быть меньше минимума' };
  }

  return {
    question: {
      ...question,
      minSelections: minParsed.value,
      maxSelections: maxParsed.value,
    },
  };
}
